import { KeyboardEventHandler, RefObject, useRef } from "react"
import { Decimal } from "shared-ts/classes/decimal"
import {
  useDecimalInput,
  UseDecimalInputProps,
} from "shared-ts/hooks/useInput/useDecimalInput"

import { TextInput, TextInputProps } from "../TextInput"

export interface TextDecimalInputProps
  extends TextInputProps,
    UseDecimalInputProps {
  onDecimalKeyDown?: KeyboardEventHandler<HTMLInputElement>
  isArrowKeysEnabled?: boolean
  ref?: RefObject<HTMLInputElement | null>
}

export const TextDecimalInput = ({
  decimal,
  onChangeDecimal,
  onBlurDecimal,
  numDecimalPlaces,
  trimTrailingZeros,
  isArrowKeysEnabled = false,
  onDecimalKeyDown,
  allowNegative,
  treatEmptyAsZero,
  ref,
  ...otherProps
}: TextDecimalInputProps) => {
  const {
    value,
    handleChange: handleDecimalChange,
    handleBlur,
    handleFocus,
  } = useDecimalInput({
    decimal,
    onChangeDecimal,
    onBlurDecimal,
    numDecimalPlaces,
    allowNegative,
    trimTrailingZeros,
    treatEmptyAsZero,
  })

  const innerRef = useRef<HTMLInputElement>(null)
  const inputRef = ref ?? innerRef

  const handleDecimalKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (decimal && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault()
      // Callers without onChangeDecimal advance on blur only so arrow keys
      // step from displayed value when onChangeDecimal is unset
      let base = decimal
      if (!onChangeDecimal) {
        try {
          base = Decimal.fromBigSource(value)
        } catch {
          base = Decimal.zero()
        }
      }
      const newDecimal =
        e.key === "ArrowUp"
          ? base.add(Decimal.fromBigSource(1))
          : base.sub(Decimal.fromBigSource(1))
      handleDecimalChange(newDecimal.toFixed(numDecimalPlaces))
    } else if (e.key === "Enter") {
      e.preventDefault()
      e.currentTarget.blur()
    }
    onDecimalKeyDown?.(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    if (
      numDecimalPlaces != null &&
      input.split(".")[1]?.length > numDecimalPlaces
    ) {
      e.preventDefault()
      return
    }
    handleDecimalChange(input)
  }

  return (
    <TextInput
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={isArrowKeysEnabled ? handleDecimalKeyDown : undefined}
      onFocus={handleFocus}
      value={value}
      ref={inputRef}
      {...otherProps}
    />
  )
}
